import type { LucideIcon } from 'lucide-react'
import { Pencil, Plus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardAction, CardContent, CardHeader } from '@/components/ui/card'
import { EntityManageSection } from '@/components/ui/entity-manage-section'
import { MarkdownBody } from '@/components/ui/markdown-content'
import { SectionTitle } from '@/components/ui/section-title'
import { stripRedundantLeadingHeading } from '@/lib/markdown-display'
import {
  profileSectionEmptyCtaClass,
  profileSectionManageButtonClass,
} from '@/lib/profile-section-styles'
import { cn } from '@/lib/utils'

export type EntityAboutSectionProps = {
  body?: string | null
  onEdit?: () => void
  emptyVariant: 'hide' | 'cta' | 'text'
  emptyCtaLabel?: string
  emptyMessage?: string
  highlighted?: boolean
  agentHighlight?: string
  title?: string
  subtitle?: string
  leadingIcon?: LucideIcon
  /** Extra strings passed to `stripRedundantLeadingHeading` (section `title` is always included). */
  stripLeadingTitles?: string[]
  editAriaLabel?: string
  editTitle?: string
  className?: string
}

/** Markdown section below identity hero — About, job description, org description, etc. */
export function EntityAboutSection({
  body,
  onEdit,
  emptyVariant,
  emptyCtaLabel = 'Add your bio',
  emptyMessage = 'No description yet — edit to add.',
  highlighted = false,
  agentHighlight,
  title = 'About',
  subtitle,
  leadingIcon: LeadingIcon,
  stripLeadingTitles,
  editAriaLabel = 'Edit about',
  editTitle = 'Edit about',
  className,
}: EntityAboutSectionProps) {
  const compactHeader = Boolean(LeadingIcon || subtitle)
  const displayBody = stripRedundantLeadingHeading(body?.trim() ?? '', [
    title,
    ...(stripLeadingTitles ?? []),
  ])
  const showMarkdown = Boolean(displayBody)

  if (!showMarkdown && emptyVariant === 'hide') {
    return null
  }

  const bodyContent = showMarkdown ? (
    <MarkdownBody>{displayBody}</MarkdownBody>
  ) : emptyVariant === 'cta' && onEdit ? (
    <Button
      type="button"
      variant="outline"
      className={profileSectionEmptyCtaClass}
      onClick={onEdit}
    >
      <Plus className="mr-2 h-4 w-4 shrink-0" aria-hidden />
      {emptyCtaLabel}
    </Button>
  ) : emptyVariant === 'text' ? (
    <p className="text-sm text-muted-foreground">{emptyMessage}</p>
  ) : null

  if (compactHeader) {
    return (
      <EntityManageSection
        title={title}
        subtitle={subtitle}
        leadingIcon={LeadingIcon}
        onEdit={onEdit}
        editAriaLabel={editAriaLabel}
        editTitle={editTitle}
        highlighted={highlighted}
        agentHighlight={agentHighlight}
        className={className}
      >
        {bodyContent}
      </EntityManageSection>
    )
  }

  return (
    <Card
      className={cn(
        'group/section relative z-0 transition-all duration-300',
        highlighted ? 'bg-success/5 ring-2 ring-success/50' : undefined,
        className,
      )}
      {...(agentHighlight ? { 'data-agent-highlight': agentHighlight } : {})}
    >
      <CardHeader className="pb-2">
        <div className="flex min-h-8 w-full items-start justify-between gap-2">
          <SectionTitle>{title}</SectionTitle>
          {onEdit ? (
            <CardAction className="row-span-1 self-center">
              <Button
                type="button"
                variant="ghostToolbar"
                size="iconSm"
                className={profileSectionManageButtonClass}
                aria-label={editAriaLabel}
                title={editTitle}
                onClick={onEdit}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </CardAction>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="pt-0">{bodyContent}</CardContent>
    </Card>
  )
}
